import { noop, throttle } from 'lodash-es';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useAuthContext, useEndpointsContext } from '../../contexts';
import { checkSessionCookieExpiration } from '../../providers/getAuthAction';
import { CustomEventType, useDocumentCustomEventListener } from '../../tokens/event';
import { Head } from '../../utils';
import { VStack } from '../Core';
import { Dialog } from '../Dialog';
import { Icon, IconName } from '../Icons';
import { FormattedMessage } from '../Intl';

const messages = defineMessages({
  sessionExpired: {
    defaultMessage: 'Session Expired',
    id: 'SessionExpiredModal.sessionExpired',
  },
  pleaseSignInAgain: {
    defaultMessage: 'Your session has expired. Please sign in again.',
    id: 'SessionExpiredModal.pleaseSignInAgain',
  },
});

export const SessionExpiredModal = ({
  sessionCookieName,
  sessionCheckRequestPath,
  disabled,
}: {
  sessionCookieName: string;
  sessionCheckRequestPath: string;
  disabled: boolean;
}) => {
  return disabled ? null : (
    <SessionExpiredModalInner sessionCookieName={sessionCookieName} sessionCheckRequestPath={sessionCheckRequestPath} />
  );
};

const SessionExpiredModalInner = ({
  sessionCookieName,
  sessionCheckRequestPath,
}: {
  sessionCookieName: string;
  sessionCheckRequestPath: string;
}) => {
  const { logout } = useAuthContext();
  const sessionCookieExpired = useSessionQuery({
    sessionCookieName,
    sessionCheckRequestPath,
  });
  const intl = useIntl();

  return (
    <Dialog
      stretchButtons
      dataTestId="session-expired-modal"
      isOpen={sessionCookieExpired}
      title={intl.formatMessage(messages.sessionExpired)}
      close={noop}
      onConfirm={logout}
      showClose={false}
      showCancel={false}
    >
      <VStack gap="spacing16">
        <Icon icon={IconName.LockClosed} size={48} />
        <FormattedMessage {...messages.pleaseSignInAgain} />
      </VStack>
    </Dialog>
  );
};

const useSessionQuery = ({
  sessionCookieName,
  sessionCheckRequestPath,
}: {
  sessionCookieName: string;
  sessionCheckRequestPath: string;
}) => {
  const [sessionCookieExpired, setSessionCookieExpired] = useState(
    checkSessionCookieExpiration(sessionCookieName).isExpired
  );
  const { apiEndpoint } = useEndpointsContext();
  const updateSessionCookieExpiration = useCallback(() => {
    const isExpired = checkSessionCookieExpiration(sessionCookieName).isExpired;
    setSessionCookieExpired(isExpired);
  }, [sessionCookieName]);

  const makeRequestAndUpdateSessionCookiedExpiration = useMemo(
    () =>
      throttle(async () => {
        if (apiEndpoint) {
          await Head(apiEndpoint, sessionCheckRequestPath);
        }
      }, 1000 * 60 /* 1 minute */),
    [apiEndpoint, sessionCheckRequestPath]
  );

  useDocumentCustomEventListener({
    eventType: CustomEventType.HTTP_UNAUTHORIZED,
    handler: updateSessionCookieExpiration,
  });

  // When the page visiblity changes, check the session cookie expiration
  useEffect(() => {
    window.addEventListener('focus', makeRequestAndUpdateSessionCookiedExpiration);
    document.addEventListener('visibilitychange', makeRequestAndUpdateSessionCookiedExpiration);
    return () => {
      document.removeEventListener('visibilitychange', makeRequestAndUpdateSessionCookiedExpiration);
      window.removeEventListener('focus', makeRequestAndUpdateSessionCookiedExpiration);
    };
  }, [makeRequestAndUpdateSessionCookiedExpiration]);

  return sessionCookieExpired;
};
